/**
 * Web-side photo sync — mirror of `mobile/src/sync/syncPhotos.ts`,
 * adapted to the browser primitives.
 *
 * Wire shapes (the relay forwards opaque encrypted blobs between
 * peers; the actual format is mobile-defined):
 *
 *   ws sync message body (after AES-CBC decrypt + JSON.parse):
 *     { type: 'photo_upsert', photo: Photo, timestamp: number }
 *     { type: 'photo_delete', photoId: string, workId: string,
 *                             timestamp: number }
 *
 *   HTTP encrypted binary (relay endpoint /photos/:id):
 *     The mobile uploads a base64 of the JPEG bytes, then encrypts
 *     that base64 string with the same `encrypt()` used for ws
 *     payloads. So the response body is `ivHex:base64(ct)`, where
 *     decrypting yields a base64 string, and decoding *that*
 *     base64 yields the raw image bytes.
 *
 *   Voice notes reuse the same /photos/:id endpoint with id
 *     prefixed `audio_${noteId}`. We don't fetch them here yet —
 *     that's a follow-up. Voice-note metadata already arrives
 *     inline in the Work upsert (work.voiceNotes), so the listing
 *     is functional; only the audio binary is missing.
 *
 * Why a Blob URL: the existing WorkDetail UI renders photos via
 * `<img src={photo.localPath} />`. By minting a `URL.createObjectURL(blob)`
 * after decrypt we slot straight into the existing render path —
 * no new image-loading machinery needed. The URL is revoked on
 * deletion to avoid the per-tab memory leak browsers track.
 *
 * Spec 200 §7 (SYNC-W1): closes the mobile→web photo
 * delivery gap surfaced by `test_210_web_pairing_photos_voice_sync`.
 */
import { downloadEncryptedBlobUrl } from './encryptedBinary';
import { savePhoto, deletePhoto } from '../store/db';
import type { Photo } from '../types';

export interface PhotoUpsertMsg {
  type: 'photo_upsert';
  photo: Photo;
  timestamp: number;
}
export interface PhotoDeleteMsg {
  type: 'photo_delete';
  photoId: string;
  workId: string;
  timestamp: number;
}
export type PhotoSyncMsg = PhotoUpsertMsg | PhotoDeleteMsg;

export function isPhotoSyncMsg(m: { type?: string } | null | undefined): m is PhotoSyncMsg {
  return !!m && (m.type === 'photo_upsert' || m.type === 'photo_delete');
}

/**
 * Fetch + decrypt a single encrypted photo binary from the relay.
 * Returns a Blob URL ready to use as an <img src>, or null when
 * the request fails. Thin wrapper over the shared
 * `downloadEncryptedBlobUrl` so the photo-specific entrypoint
 * stays self-documenting at the call site.
 */
export async function downloadPhotoBlobUrl(
  photoId: string,
  teamKeyHex: string,
  companyToken: string,
): Promise<string | null> {
  return downloadEncryptedBlobUrl(photoId, teamKeyHex, companyToken, 'image/jpeg');
}

/**
 * Apply a parsed photo sync message to local state. Persists
 * metadata first (so the grid shows a placeholder thumb if any),
 * then fires the HTTP fetch + decrypt and patches the row's
 * `localPath` once the Blob URL is ready. Both writes go through
 * `savePhoto`, which emits the store change so WorkDetail re-renders.
 *
 * The function never throws — any failure short-circuits to a
 * silent return so a single bad photo can't tear the live-sync
 * handler down.
 */
export async function applyPhotoMessage(
  message: PhotoSyncMsg,
  teamKeyHex: string,
  companyToken: string,
): Promise<void> {
  if (message.type === 'photo_delete') {
    try {
      await deletePhoto(message.photoId);
    } catch {
      // best-effort; storage hiccups don't justify a hard failure here.
    }
    return;
  }

  // photo_upsert
  const incoming = { ...message.photo };
  // Mobile sets localPath to the device-specific path; that means
  // nothing to the browser, so reset to empty until we fetch.
  incoming.localPath = '';
  try {
    await savePhoto(incoming);
  } catch {
    return; // can't proceed without the metadata row
  }
  const url = await downloadPhotoBlobUrl(incoming.id, teamKeyHex, companyToken);
  if (!url) return;
  try {
    await savePhoto({ ...incoming, localPath: url });
  } catch {
    URL.revokeObjectURL(url);
  }
}
